import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { lumi } from '../lib/lumi'

export default function OrderTrackingPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [orderId, setOrderId] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<any>(null)
  const [error, setError] = useState('')

  // 判斷當前語言
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh'

  // 翻譯對象
  const t: Record<string, Record<string, string>> = {
    zh: {
      title: '訂單查詢',
      subtitle: '輸入您的訂單編號和電子郵件地址以查詢訂單狀態',
      orderNumber: '訂單編號',
      orderNumberPlaceholder: '請輸入訂單編號',
      email: '電子郵件',
      emailPlaceholder: '請輸入電子郵件地址',
      search: '查詢訂單',
      searching: '查詢中...',
      notFound: '找不到訂單，請檢查訂單編號和電子郵件是否正確',
      orderDetails: '訂單詳情',
      orderDate: '訂單日期',
      status: '訂單狀態',
      paymentMethod: '付款方式',
      orderItems: '訂單商品',
      quantity: '數量',
      subtotal: '小計',
      shipping: '運費',
      discount: '折扣',
      pointsUsed: '使用點數',
      total: '總計',
      shippingInfo: '收件資訊',
      recipient: '收件人',
      phone: '電話',
      address: '地址',
      backToSearch: '返回查詢',
      goToMember: '前往會員中心'
    },
    en: {
      title: 'Order Tracking',
      subtitle: 'Enter your order number and email address to track your order',
      orderNumber: 'Order Number',
      orderNumberPlaceholder: 'Enter order number',
      email: 'Email',
      emailPlaceholder: 'Enter email address',
      search: 'Track Order',
      searching: 'Searching...',
      notFound: 'Order not found. Please check your order number and email',
      orderDetails: 'Order Details',
      orderDate: 'Order Date',
      status: 'Order Status',
      paymentMethod: 'Payment Method',
      orderItems: 'Order Items',
      quantity: 'Qty',
      subtotal: 'Subtotal',
      shipping: 'Shipping',
      discount: 'Discount',
      pointsUsed: 'Points Used',
      total: 'Total',
      shippingInfo: 'Shipping Information',
      recipient: 'Recipient',
      phone: 'Phone',
      address: 'Address',
      backToSearch: 'Back to Search',
      goToMember: 'Go to Member Center'
    }
  }

  // 從 URL 參數讀取訂單資訊並自動查詢
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const urlOrderId = params.get('orderid') || params.get('orderId')
    const urlEmail = params.get('email')

    if (urlOrderId && urlEmail) {
      setOrderId(urlOrderId)
      setEmail(decodeURIComponent(urlEmail))
      
      // 自動執行查詢
      const autoSearch = async () => {
        setLoading(true)
        setError('')

        try {
          const response = await fetch(
            `https://api.lumi.new/v1/functions/p373839333138763776/getOrderByNumber?orderNumber=${encodeURIComponent(urlOrderId.trim())}`
          )
          const data = await response.json()

          if (data.success && data.order) {
            setOrder(data.order)
          } else {
            setError(t[currentLang].notFound)
          }
        } catch (err: any) {
          console.error('Order search error:', err)
          setError(t[currentLang].notFound)
        } finally {
          setLoading(false)
        }
      }

      autoSearch()
    }
  }, [location.search, currentLang])

  // 訂單狀態映射
  const statusLabels: Record<string, Record<string, string>> = {
    zh: {
      pending: "待付款",
      paid: "已付款",
      processing: "處理中",
      shipped: "已出貨",
      completed: "已完成",
      cancelled: "已取消",
      refunded: "已退款",
      payment_failed: "付款失敗"
    },
    en: {
      pending: "Pending Payment",
      paid: "Paid",
      processing: "Processing",
      shipped: "Shipped",
      completed: "Completed",
      cancelled: "Cancelled",
      refunded: "Refunded",
      payment_failed: "Payment Failed"
    }
  }

  // 狀態顏色
  const statusColors: Record<string, string> = {
    pending: "bg-yellow-800 text-yellow-100",
    paid: "bg-blue-800 text-blue-100",
    processing: "bg-purple-800 text-purple-100",
    shipped: "bg-orange-800 text-orange-100",
    completed: "bg-green-800 text-green-100",
    cancelled: "bg-red-800 text-red-100",
    refunded: "bg-gray-700 text-gray-200",
    payment_failed: "bg-red-900 text-red-100"
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!orderId.trim() || !email.trim()) {
      setError(t[currentLang].notFound)
      return
    }

    setLoading(true)
    setError('')
    setOrder(null)

    try {
      const response = await fetch(
        `https://api.lumi.new/v1/functions/p373839333138763776/getOrderByNumber?orderNumber=${encodeURIComponent(orderId.trim())}`
      )
      const data = await response.json()

      if (data.success && data.order) {
        setOrder(data.order)
      } else {
        setError(t[currentLang].notFound)
      }
    } catch (err: any) {
      console.error('Order search error:', err)
      setError(t[currentLang].notFound)
    } finally {
      setLoading(false)
    }
  }

  const resetSearch = () => {
    setOrder(null)
    setOrderId('')
    setEmail('')
    setError('')
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* 標題 */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-extralight tracking-wider text-black dark:text-white mb-4">
            {t[currentLang].title}
          </h1>
          <p className="text-sm font-light text-gray-600 dark:text-gray-400">
            {t[currentLang].subtitle}
          </p>
        </div>

        {!order ? (
          /* 查詢表單 */
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-8">
            <form onSubmit={handleSearch} className="space-y-6">
              {/* 訂單編號 */}
              <div>
                <label className="block text-sm font-light text-gray-700 dark:text-gray-300 mb-2">
                  {t[currentLang].orderNumber}
                </label>
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder={t[currentLang].orderNumberPlaceholder}
                  className="w-full px-4 py-3 bg-white dark:bg-black border border-gray-300 dark:border-gray-700 text-black dark:text-white font-light focus:outline-none focus:border-gray-500"
                  required
                />
              </div>

              {/* 電子郵件 */}
              <div>
                <label className="block text-sm font-light text-gray-700 dark:text-gray-300 mb-2">
                  {t[currentLang].email}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t[currentLang].emailPlaceholder}
                  className="w-full px-4 py-3 bg-white dark:bg-black border border-gray-300 dark:border-gray-700 text-black dark:text-white font-light focus:outline-none focus:border-gray-500"
                  required
                />
              </div>

              {/* 錯誤訊息 */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3">
                  <p className="text-sm font-light text-red-800 dark:text-red-200">
                    {error}
                  </p>
                </div>
              )}

              {/* 查詢按鈕 */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black dark:bg-white text-white dark:text-black py-4 font-light tracking-wider hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t[currentLang].searching : t[currentLang].search}
              </button>
            </form>
          </div>
        ) : (
          /* 訂單詳情 */
          <div className="space-y-6">
            {/* 訂單資訊卡 */}
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-8">
              <h2 className="text-xl font-light tracking-wider text-black dark:text-white mb-6">
                {t[currentLang].orderDetails}
              </h2>

              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-gray-200 dark:border-gray-800">
                  <span className="text-sm font-light text-gray-600 dark:text-gray-400">
                    {t[currentLang].orderNumber}
                  </span>
                  <span className="text-sm font-light text-black dark:text-white">
                    {order.orderNumber}
                  </span>
                </div>

                <div className="flex justify-between py-3 border-b border-gray-200 dark:border-gray-800">
                  <span className="text-sm font-light text-gray-600 dark:text-gray-400">
                    {t[currentLang].orderDate}
                  </span>
                  <span className="text-sm font-light text-black dark:text-white">
                    {new Date(order.createdAt).toLocaleDateString(currentLang === 'en' ? 'en-US' : 'zh-TW')}
                  </span>
                </div>

                <div className="flex justify-between py-3 border-b border-gray-200 dark:border-gray-800">
                  <span className="text-sm font-light text-gray-600 dark:text-gray-400">
                    {t[currentLang].status}
                  </span>
                  <span className={`text-xs px-3 py-1 ${statusColors[order.status] || statusColors.pending}`}>
                    {statusLabels[currentLang][order.status] || order.status}
                  </span>
                </div>

                <div className="flex justify-between py-3 border-b border-gray-200 dark:border-gray-800">
                  <span className="text-sm font-light text-gray-600 dark:text-gray-400">
                    {t[currentLang].paymentMethod}
                  </span>
                  <span className="text-sm font-light text-black dark:text-white">
                    {order.paymentMethod || '信用卡'}
                  </span>
                </div>
              </div>
            </div>

            {/* 訂單商品 */}
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-8">
              <h3 className="text-lg font-light tracking-wider text-black dark:text-white mb-6">
                {t[currentLang].orderItems}
              </h3>
              <div className="space-y-4">
                {order.items?.map((item: any, index: number) => (
                  <div key={index} className="flex justify-between py-3 border-b border-gray-200 dark:border-gray-800 last:border-b-0">
                    <div className="flex-1">
                      <p className="text-sm font-light text-black dark:text-white">
                        {item.productName}
                      </p>
                      <p className="text-xs font-light text-gray-600 dark:text-gray-400 mt-1">
                        {t[currentLang].quantity}: {item.quantity}
                      </p>
                    </div>
                    <div className="text-sm font-light text-black dark:text-white">
                      NT$ {item.price?.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              {/* 金額計算 */}
              <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-700 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="font-light text-gray-600 dark:text-gray-400">{t[currentLang].subtotal}</span>
                  <span className="font-light text-black dark:text-white">NT$ {order.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-light text-gray-600 dark:text-gray-400">{t[currentLang].shipping}</span>
                  <span className="font-light text-black dark:text-white">NT$ {order.shippingFee?.toLocaleString()}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="font-light text-gray-600 dark:text-gray-400">{t[currentLang].discount}</span>
                    <span className="font-light text-red-600 dark:text-red-400">-NT$ {order.discount?.toLocaleString()}</span>
                  </div>
                )}
                {order.pointsUsed > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="font-light text-gray-600 dark:text-gray-400">{t[currentLang].pointsUsed}</span>
                    <span className="font-light text-red-600 dark:text-red-400">-NT$ {order.pointsDiscount?.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-base pt-3 border-t border-gray-300 dark:border-gray-700">
                  <span className="font-light text-black dark:text-white">{t[currentLang].total}</span>
                  <span className="font-light text-black dark:text-white">NT$ {order.totalAmount?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* 收件資訊 */}
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-8">
              <h3 className="text-lg font-light tracking-wider text-black dark:text-white mb-6">
                {t[currentLang].shippingInfo}
              </h3>
              <div className="space-y-2 text-sm font-light text-black dark:text-white">
                <p><span className="text-gray-600 dark:text-gray-400">{t[currentLang].recipient}:</span> {order.shippingAddress?.recipient || order.recipientName}</p>
                <p><span className="text-gray-600 dark:text-gray-400">{t[currentLang].phone}:</span> {order.shippingAddress?.phone || order.recipientPhone}</p>
                <p><span className="text-gray-600 dark:text-gray-400">{t[currentLang].address}:</span> {order.shippingAddress?.fullAddress || ''}{order.shippingAddress?.city ? ` ${order.shippingAddress.city}` : ''}{order.shippingAddress?.district ? ` ${order.shippingAddress.district}` : ''}{order.shippingAddress?.postalCode ? ` ${order.shippingAddress.postalCode}` : ''}</p>
              </div>
            </div>

            {/* 操作按鈕 */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={resetSearch}
                className="flex-1 bg-white dark:bg-black text-black dark:text-white border border-gray-300 dark:border-gray-700 py-4 font-light tracking-wider hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
              >
                {t[currentLang].backToSearch}
              </button>
              <button
                onClick={() => navigate(`/${currentLang}/member/orders`)}
                className="flex-1 bg-black dark:bg-white text-white dark:text-black py-4 font-light tracking-wider hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
              >
                {t[currentLang].goToMember}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
